import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { StatusBadge, Modal, ScoreDisplay, MatchUpdateForm } from '../../components/common';
import AdminNav from '../../components/admin/AdminNav';

export default function AdminMatches() {
  const { matches, tournaments, players, addMatch, updateScore, updateMatch, fetchMatches, fetchTournaments, fetchPlayers } = useApp();
  const [modal, setModal] = useState(false);
  const [scoreModal, setScoreModal] = useState(null);
  const [form, setForm] = useState({ tournamentId: '', round: '', player1: '', player2: '', date: '', time: '', court: '' });
  const [scoreForm, setScoreForm] = useState({ sets: [], status: 'live', winner: '' });
  const [filter, setFilter] = useState('all');
  const [tournamentFilter, setTournamentFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [flashCell, setFlashCell] = useState(null);
  const [player1Search, setPlayer1Search] = useState('');
  const [player2Search, setPlayer2Search] = useState('');
  const [showPlayer1Dropdown, setShowPlayer1Dropdown] = useState(false);
  const [showPlayer2Dropdown, setShowPlayer2Dropdown] = useState(false);
  const tapTimer = React.useRef(null);

  useEffect(() => {
    if (matches.length === 0) fetchMatches();
    if (tournaments.length === 0) fetchTournaments();
    if (players.length === 0) fetchPlayers();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.player-dropdown-container')) {
        setShowPlayer1Dropdown(false);
        setShowPlayer2Dropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddMatch = (e) => {
    e.preventDefault();
    addMatch(form);
    setModal(false);
    setForm({ tournamentId: '', round: '', player1: '', player2: '', date: '', time: '', court: '' });
    setPlayer1Search('');
    setPlayer2Search('');
  };

  const openScoreEditor = (match) => {
    const defaultDate = match.date || new Date().toISOString().split('T')[0];
    const now = new Date();
    const defaultTime = match.time || `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const hasBothPlayers = match.player1 && match.player2;
    const defaultStatus = hasBothPlayers && match.status === 'scheduled' ? 'live' : match.status;
    
    setScoreModal(match);
    setScoreForm({ 
      sets: match.score.sets.length ? match.score.sets.map(s => ({ ...s })) : [{ p1: 0, p2: 0 }], 
      status: defaultStatus, 
      winner: match.winner || '', 
      date: defaultDate, 
      time: defaultTime, 
      court: match.court || '', 
      specialResult: '' 
    });
  };

  const handleScoreUpdate = async () => {
    if (scoreForm.status === 'completed' && !scoreForm.specialResult && !scoreForm.winner) {
      return;
    }
    
    try {
      if (scoreForm.specialResult && scoreForm.winner) {
        // Handle special result if needed
      }
      await updateScore(scoreModal.id, { sets: scoreForm.sets }, scoreForm.status, scoreForm.winner || null);
      await updateMatch(scoreModal.id, { date: scoreForm.date, time: scoreForm.time, court: scoreForm.court });
      setScoreModal(null);
      fetchMatches();
    } catch (e) {
      console.error(e);
    }
  };

  const quickUpdate = async (m, setIdx, pKey, delta) => {
    const sets = m.score.sets.map(s => ({ ...s }));
    sets[setIdx][pKey] = Math.max(0, Math.min(99, (sets[setIdx][pKey] || 0) + delta));
    const cellId = `${m.id}-${setIdx}-${pKey}`;
    setFlashCell({ id: cellId, color: delta > 0 ? '#43A047' : '#E53935' });
    setTimeout(() => setFlashCell(null), 400);
    try {
      await updateScore(m.id, { sets }, 'live', null);
      await fetchMatches();
    } catch (e) { console.error(e); }
  };

  const addSet = () => setScoreForm(prev => ({ ...prev, sets: [...prev.sets, { p1: 0, p2: 0 }] }));
  const removeSet = () => setScoreForm(prev => ({ ...prev, sets: prev.sets.slice(0, -1) }));
  const incSet = (i, w) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: Math.min(99, (sets[i][w] || 0) + 1) }; setScoreForm(prev => ({ ...prev, sets })); };
  const decSet = (i, w) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: Math.max(0, (sets[i][w] || 0) - 1) }; setScoreForm(prev => ({ ...prev, sets })); };
  const updSet = (i, w, v) => { const sets = [...scoreForm.sets]; sets[i] = { ...sets[i], [w]: parseInt(v) || 0 }; setScoreForm(prev => ({ ...prev, sets })); };

  let filtered = filter === 'all' ? matches : matches.filter(m => m.status === filter);
  if (tournamentFilter !== 'all') {
    filtered = filtered.filter(m => m.tournamentId === tournamentFilter);
  }
  if (search) {
    filtered = filtered.filter(m => {
      const p1 = players.find(p => p.id === m.player1);
      const p2 = players.find(p => p.id === m.player2);
      const t = tournaments.find(t => t.id === m.tournamentId);
      return (p1?.name.toLowerCase().includes(search.toLowerCase()) || 
              p2?.name.toLowerCase().includes(search.toLowerCase()) ||
              t?.name.toLowerCase().includes(search.toLowerCase()));
    });
  }

  return (
    <div style={styles.container}>
      <AdminNav />
      <div style={styles.header}>
        <h1 style={styles.title}>Matches</h1>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <input style={styles.search} placeholder="Search matches..." value={search} onChange={e => setSearch(e.target.value)} />
        <button onClick={() => setModal(true)} style={styles.addBtn}>{isMobile ? '+ Add' : '+ New Match'}</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={styles.label}>Filter by Tournament</label>
        <select style={styles.filterSelect} value={tournamentFilter} onChange={e => setTournamentFilter(e.target.value)}>
          <option value="all">All Tournaments</option>
          {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }} className="hide-scrollbar">
        {['all', 'live', 'scheduled', 'completed'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ ...styles.filterBtn, ...(filter === f ? styles.filterActive : {}) }}>
            {f === 'live' && '🔴 '}{f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <div style={styles.grid}>
        {filtered.map(m => {
          const t = tournaments.find(t => t.id === m.tournamentId);
          const p1 = players.find(p => p.id === m.player1);
          const p2 = players.find(p => p.id === m.player2);
          const isLive = m.status === 'live';
          const isDone = m.status === 'completed';
          const isUpcoming = m.status === 'scheduled';
          const sets = m.score?.sets || [];
          
          const renderPlayer = (player, pKey, oKey) => {
            const isWinner = isDone && m.winner === player?.id;
            const isLeading = isLive && sets.length > 0 && (pKey === 'p1' ? sets.reduce((s, sc) => s + (sc.p1 || 0), 0) > sets.reduce((s, sc) => s + (sc.p2 || 0), 0) : sets.reduce((s, sc) => s + (sc.p2 || 0), 0) > sets.reduce((s, sc) => s + (sc.p1 || 0), 0));
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', background: isWinner ? 'rgba(67,160,71,0.08)' : isLeading ? 'rgba(189,255,0,0.05)' : 'transparent', borderLeft: isWinner ? '3px solid #43A047' : '3px solid transparent' }}>
                <span style={{ flex: 1, fontSize: 12, fontWeight: isWinner || isLeading ? 700 : 500, color: isWinner ? '#43A047' : '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {player?.name || 'TBD'}
                </span>
                {isWinner && (
                  <span style={{ fontSize: 8, fontWeight: 800, color: '#fff', background: '#43A047', padding: '2px 6px', borderRadius: 3, letterSpacing: 0.5 }}>WON</span>
                )}
                <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {sets.map((sc, i) => {
                    const mine = sc[pKey], opp = sc[oKey];
                    const won = mine > opp;
                    const isCurrent = isLive && i === sets.length - 1;
                    const cellId = `${m.id}-${i}-${pKey}`;
                    const isFlash = flashCell?.id === cellId;
                    const canTap = isLive;
                    return (
                      <span key={i}
                        onClick={canTap ? (e) => { e.stopPropagation(); if (tapTimer.current) { clearTimeout(tapTimer.current); tapTimer.current = null; quickUpdate(m, i, pKey, -1); } else { tapTimer.current = setTimeout(() => { tapTimer.current = null; quickUpdate(m, i, pKey, 1); }, 250); } } : undefined}
                        onContextMenu={canTap ? (e) => { e.preventDefault(); } : undefined}
                        style={{
                        width: isCurrent ? 30 : 24, height: isCurrent ? 30 : 24,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: isCurrent ? 15 : 12,
                        fontWeight: isCurrent ? 800 : won ? 700 : 400,
                        fontFamily: "'Outfit', sans-serif",
                        color: isFlash ? '#fff' : isCurrent ? '#E53935' : won ? '#00205B' : '#B0B8C9',
                        background: isFlash ? flashCell.color : isCurrent ? 'rgba(229,57,53,0.1)' : won ? '#EEF1F6' : 'transparent',
                        borderRadius: 6,
                        border: isCurrent ? '1.5px solid rgba(229,57,53,0.3)' : '1.5px solid transparent',
                        cursor: canTap ? 'pointer' : 'default',
                        transition: 'all 0.2s',
                        userSelect: 'none'
                      }}>{mine}</span>
                    );
                  })}
                </div>
              </div>
            );
          };
          
          return (
            <div key={m.id} style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${isLive ? '#E53935' : isDone ? '#43A047' : '#00205B'}`, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 14px', background: '#F8F9FC', borderBottom: '1px solid #EEF1F6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isLive && <>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#E53935', animation: 'pulse 1.5s infinite' }} />
                    <span style={{ fontSize: 11, fontWeight: 800, color: '#E53935', letterSpacing: 1 }}>LIVE</span>
                    {sets.length > 0 && <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600, background: '#EEF1F6', padding: '1px 7px', borderRadius: 4 }}>Set {sets.length}</span>}
                  </>}
                  {isUpcoming && <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600, background: '#EEF1F6', padding: '1px 7px', borderRadius: 4 }}>Upcoming</span>}
                  {isDone && sets.length > 0 && <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600, background: '#EEF1F6', padding: '1px 7px', borderRadius: 4 }}>{sets.length} Set{sets.length > 1 ? 's' : ''}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600 }}>{t?.name}</span>
                  {m.round && <span style={{ fontSize: 10, fontWeight: 700, color: '#00205B', background: '#EEF1F6', padding: '2px 8px', borderRadius: 4 }}>{m.round}</span>}
                </div>
              </div>
              <div>
                {isUpcoming ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px' }}>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p1?.name || 'TBD'}
                      </span>
                    </div>
                    <div style={{ height: 1, background: '#EEF1F6', margin: '0 10px' }} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px' }}>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#00205B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p2?.name || 'TBD'}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    {sets.length > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 10px 0', gap: 3, alignItems: 'center' }}>
                        {isLive && <span onClick={async (e) => { e.stopPropagation(); const newSets = [...sets.map(s => ({ ...s })), { p1: 0, p2: 0 }]; try { await updateScore(m.id, { sets: newSets }, 'live', null); fetchMatches(); } catch {} }} style={{ width: 22, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#B0B8C9', background: '#F4F6FA', borderRadius: 4, cursor: 'pointer', userSelect: 'none', border: '1px dashed #D8DDE8' }} title="Add Set">+</span>}
                        {sets.map((_, i) => <span key={i} style={{ width: 28, textAlign: 'center', fontSize: 8, fontWeight: 700, color: '#B0B8C9', letterSpacing: 0.5 }}>S{i + 1}</span>)}
                      </div>
                    )}
                    {renderPlayer(p1, 'p1', 'p2')}
                    <div style={{ height: 1, background: '#EEF1F6', margin: '0 16px' }} />
                    {renderPlayer(p2, 'p2', 'p1')}
                  </>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 14px', borderTop: '1px solid #EEF1F6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  {m.date && (
                    <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600 }}>
                      {new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  )}
                  {m.time && (
                    <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600 }}>
                      {m.time}
                    </span>
                  )}
                  {m.court && (
                    <span style={{ fontSize: 10, color: '#8892A6', fontWeight: 600, background: '#EEF1F6', padding: '2px 6px', borderRadius: 4 }}>
                      {m.court}
                    </span>
                  )}
                </div>
                <button onClick={() => openScoreEditor(m)} style={styles.scoreBtn}>
                  {isUpcoming ? '▶ Start' : '✏️ Update'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Modal isOpen={modal} onClose={() => setModal(false)} title="Create Match">
        <form onSubmit={handleAddMatch}>
          {/* Tournament Selection */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Tournament <span style={{ color: '#E53935' }}>*</span></label>
            <select className="form-input" value={form.tournamentId} onChange={e => setForm(p => ({ ...p, tournamentId: e.target.value }))} required>
              <option value="">Select tournament...</option>
              {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          {/* Round Selection */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Round <span style={{ color: '#E53935' }}>*</span></label>
            <select className="form-input" value={form.round} onChange={e => setForm(p => ({ ...p, round: e.target.value }))} required>
              <option value="">Select round...</option>
              {['Round of 16', 'Quarter Final', 'Semi Final', 'Final', 'Group Stage'].map(r => <option key={r}>{r}</option>)}
            </select>
          </div>

          {/* Players Section */}
          <div style={{ background: '#F8F9FC', borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div className="form-label" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 18 }}>🎾</span> Match Players
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {/* Player 1 */}
              <div style={{ position: 'relative' }} className="player-dropdown-container">
                <label className="form-label">Player 1 <span style={{ color: '#E53935' }}>*</span></label>
                <div
                  onClick={() => setShowPlayer1Dropdown(!showPlayer1Dropdown)}
                  style={{ padding: '10px 12px', border: '1.5px solid #D8DDE8', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 42 }}
                >
                  <span style={{ color: form.player1 ? '#00205B' : '#8892A6', fontWeight: form.player1 ? 600 : 400 }}>
                    {form.player1 ? players.find(p => p.id === form.player1)?.name : 'Select player...'}
                  </span>
                  <span style={{ fontSize: 10, color: '#8892A6' }}>▼</span>
                </div>
                {showPlayer1Dropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #EEF1F6', borderRadius: 8, marginTop: 4, zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #EEF1F6' }}>
                      <input
                        type="text"
                        placeholder="Search player..."
                        value={player1Search}
                        onChange={e => setPlayer1Search(e.target.value)}
                        autoFocus
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #D8DDE8', borderRadius: 6, fontSize: 13, outline: 'none' }}
                      />
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {players
                        .filter(p => p.name.toLowerCase().includes(player1Search.toLowerCase()) && p.id !== form.player2)
                        .map(p => (
                          <div
                            key={p.id}
                            onClick={() => {
                              setForm(prev => ({ ...prev, player1: p.id }));
                              setPlayer1Search('');
                              setShowPlayer1Dropdown(false);
                            }}
                            style={{ padding: '10px 12px', cursor: 'pointer', fontSize: 13, color: '#00205B', borderBottom: '1px solid #F8F9FC', transition: 'background 0.2s', fontWeight: form.player1 === p.id ? 700 : 400, background: form.player1 === p.id ? '#F8F9FC' : 'transparent' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F8F9FC'}
                            onMouseLeave={e => e.currentTarget.style.background = form.player1 === p.id ? '#F8F9FC' : 'transparent'}
                          >
                            {p.name}
                            {form.player1 === p.id && <span style={{ marginLeft: 8, color: '#43A047' }}>✓</span>}
                          </div>
                        ))}
                      {players.filter(p => p.name.toLowerCase().includes(player1Search.toLowerCase()) && p.id !== form.player2).length === 0 && (
                        <div style={{ padding: '20px 12px', fontSize: 12, color: '#8892A6', textAlign: 'center' }}>No players found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Player 2 */}
              <div style={{ position: 'relative' }} className="player-dropdown-container">
                <label className="form-label">Player 2 <span style={{ color: '#E53935' }}>*</span></label>
                <div
                  onClick={() => setShowPlayer2Dropdown(!showPlayer2Dropdown)}
                  style={{ padding: '10px 12px', border: '1.5px solid #D8DDE8', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 42 }}
                >
                  <span style={{ color: form.player2 ? '#00205B' : '#8892A6', fontWeight: form.player2 ? 600 : 400 }}>
                    {form.player2 ? players.find(p => p.id === form.player2)?.name : 'Select player...'}
                  </span>
                  <span style={{ fontSize: 10, color: '#8892A6' }}>▼</span>
                </div>
                {showPlayer2Dropdown && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #EEF1F6', borderRadius: 8, marginTop: 4, zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #EEF1F6' }}>
                      <input
                        type="text"
                        placeholder="Search player..."
                        value={player2Search}
                        onChange={e => setPlayer2Search(e.target.value)}
                        autoFocus
                        style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #D8DDE8', borderRadius: 6, fontSize: 13, outline: 'none' }}
                      />
                    </div>
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {players
                        .filter(p => p.name.toLowerCase().includes(player2Search.toLowerCase()) && p.id !== form.player1)
                        .map(p => (
                          <div
                            key={p.id}
                            onClick={() => {
                              setForm(prev => ({ ...prev, player2: p.id }));
                              setPlayer2Search('');
                              setShowPlayer2Dropdown(false);
                            }}
                            style={{ padding: '10px 12px', cursor: 'pointer', fontSize: 13, color: '#00205B', borderBottom: '1px solid #F8F9FC', transition: 'background 0.2s', fontWeight: form.player2 === p.id ? 700 : 400, background: form.player2 === p.id ? '#F8F9FC' : 'transparent' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F8F9FC'}
                            onMouseLeave={e => e.currentTarget.style.background = form.player2 === p.id ? '#F8F9FC' : 'transparent'}
                          >
                            {p.name}
                            {form.player2 === p.id && <span style={{ marginLeft: 8, color: '#43A047' }}>✓</span>}
                          </div>
                        ))}
                      {players.filter(p => p.name.toLowerCase().includes(player2Search.toLowerCase()) && p.id !== form.player1).length === 0 && (
                        <div style={{ padding: '20px 12px', fontSize: 12, color: '#8892A6', textAlign: 'center' }}>No players found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Schedule Section */}
          <div style={{ background: '#F8F9FC', borderRadius: 10, padding: 14, marginBottom: 16 }}>
            <div className="form-label" style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 18 }}>📅</span> Schedule Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label className="form-label">Date <span style={{ color: '#E53935' }}>*</span></label>
                <input className="form-input" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} required />
              </div>
              <div>
                <label className="form-label">Time <span style={{ color: '#E53935' }}>*</span></label>
                <input className="form-input" type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} required />
              </div>
            </div>
            <div>
              <label className="form-label">Court <span style={{ color: '#E53935' }}>*</span></label>
              <input className="form-input" value={form.court} onChange={e => setForm(p => ({ ...p, court: e.target.value }))} placeholder="e.g. Center Court" required />
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" style={{ width: '100%', padding: '12px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', letterSpacing: 0.5, fontFamily: "'Outfit', sans-serif", transition: 'all 0.2s' }}>
            Create Match
          </button>
        </form>
      </Modal>

      <Modal isOpen={!!scoreModal} onClose={() => setScoreModal(null)} title="Update Match">
        {scoreModal && (() => {
          const t1 = scoreModal.player1 ? { name: players.find(p => p.id === scoreModal.player1)?.name || 'TBD' } : { name: 'TBD' };
          const t2 = scoreModal.player2 ? { name: players.find(p => p.id === scoreModal.player2)?.name || 'TBD' } : { name: 'TBD' };
          return (
            <MatchUpdateForm
              match={scoreModal}
              t1={t1}
              t2={t2}
              scoreForm={scoreForm}
              setScoreForm={setScoreForm}
              addSet={addSet}
              removeSet={removeSet}
              decSet={decSet}
              incSet={incSet}
              updSet={updSet}
              saveScore={handleScoreUpdate}
              savingScore={false}
              scoreError={''}
            />
          );
        })()}
      </Modal>
    </div>
  );
}

const styles = {
  container: { minHeight: 'calc(100vh - 52px)', background: 'var(--bg)', padding: '16px' },
  header: { marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif", marginBottom: 4 },
  search: { padding: '8px 12px', border: '1px solid #EEF1F6', borderRadius: 8, fontSize: 13, outline: 'none', flex: 1, minWidth: 140, background: '#fff' },
  addBtn: { padding: '10px 24px', background: '#BDFF00', color: '#00205B', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  filterSelect: { width: '100%', padding: 10, border: '1px solid #EEF1F6', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff' },
  filterBtn: { padding: '8px 16px', border: '1px solid #EEF1F6', borderRadius: 20, background: '#fff', cursor: 'pointer', fontSize: 13, flexShrink: 0, whiteSpace: 'nowrap' },
  filterActive: { background: '#00205B', color: '#BDFF00', borderColor: '#00205B' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 },
  scoreBtn: { padding: '6px 14px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 },
};
